import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  CircularProgress,
  Box,
  Typography,
  Chip,
  Grid,
  Divider,
  MenuItem,
  Paper,
  Alert,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { toast } from "react-toastify";
import { addDays, getDay, format, startOfDay, subYears } from "date-fns";
import { usePermissions } from "../../hooks/usePermissions";
import {
  TIMETABLE_CONVERT,
  TIMETABLE_CONVERT_OWN,
} from "../../utils/timetablePermissions";
import { DATE_DISPLAY_FORMAT } from "../../utils/dateUtils";
import conversionService from "../../services/conversionService";
import api from "../../services/api";
import courseService from "../../services/courseService";

/**
 * Map dayOfWeek string/number from slot to JS Date getDay() value (0=Sun, 1=Mon, ...).
 * The backend stores dayOfWeek as a string like "Monday", "Tuesday", etc.
 * Mirrors EditLectureForm.js / ConvertLabToTheoryModal.js conventions.
 */
const DAY_NAME_TO_NUMBER = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

const MIN_BATCHES = 2;
const MAX_BATCHES = 10;
const DEFAULT_BATCHES = 3;
const SHORT_LAB_MINUTES = 30;

function getDayNumber(dayOfWeek) {
  if (dayOfWeek == null) return -1;
  if (typeof dayOfWeek === "number") return dayOfWeek;
  const normalized = String(dayOfWeek).toLowerCase().trim();
  return DAY_NAME_TO_NUMBER[normalized] ?? -1;
}

/** Parse an "HH:MM" (24h) string into minutes-since-midnight. Returns NaN when malformed. */
function timeToMinutes(hhmm) {
  if (typeof hhmm !== "string") return NaN;
  const m = hhmm.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return NaN;
  const hours = Number(m[1]);
  const mins = Number(m[2]);
  if (
    Number.isNaN(hours) ||
    Number.isNaN(mins) ||
    hours < 0 ||
    hours > 23 ||
    mins < 0 ||
    mins > 59
  ) {
    return NaN;
  }
  return hours * 60 + mins;
}

/**
 * Normalize a raw slot time value (may be "HH:MM", "HH:MM:SS", Date-ish) to "HH:MM".
 * Falls back to empty string when it can't be parsed.
 */
function normalizeTime(value) {
  if (value == null) return "";
  const str = String(value).trim();
  const m = str.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return "";
  return `${String(Number(m[1])).padStart(2, "0")}:${m[2]}`;
}

/** Human-friendly duration label: "1 hour", "2 hours", "1 hour 30 min", "45 min". */
function formatDuration(totalMinutes) {
  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) return "";
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  const parts = [];
  if (hours > 0) parts.push(`${hours} hour${hours > 1 ? "s" : ""}`);
  if (mins > 0) parts.push(`${mins} min`);
  return parts.join(" ");
}

/** Default batch code for index i: A1, A2, A3, ... */
function defaultBatchCode(index) {
  return `A${index + 1}`;
}

/** Helper to avoid nested ternary in JSX for the date field. */
function getDateHelperText(isDateValid, overrideDate, dayOfWeek) {
  if (!isDateValid && overrideDate) {
    return "Date must match the slot's weekday (past dates allowed, up to 7 days ahead)";
  }
  return `Only ${dayOfWeek || "matching"} dates allowed`;
}

/** Retry-aware helper text for the async dropdowns (teachers/subjects). */
function getListHelperText(errorText, onRetry) {
  if (!errorText) return null;
  return (
    <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {errorText}
      <Chip label="Retry" size="small" onClick={onRetry} sx={{ ml: 1, cursor: "pointer" }} />
    </Box>
  );
}

/**
 * ConvertTheoryToLabModal — Modal to convert 1+ consecutive theory lectures into a
 * parallel-batch lab block (THEORY_TO_LAB) for a specific date.
 *
 * Mirrors ConvertLabToTheoryModal.js conventions (MUI Dialog/Autocomplete/DatePicker,
 * date-fns, react-toastify, usePermissions, teacher list via
 * `/teacher/get-teachers-names`, subjects via courseService.getAllSubjects).
 *
 * The target lab spans the COMBINED source span: for a single source slot it is that
 * slot's own start/end; for multiple consecutive slots it runs from the first slot's
 * start to the last slot's end (sorted by startTime). All batch lab slots are parallel
 * and share this same start/end.
 *
 * Props:
 * - open: boolean — controls dialog visibility
 * - onClose: () => void — called when the dialog should close
 * - sourceSlots: array — 1+ consecutive theory slot objects; each has
 *     { id, teacher: {id,name}, subject: {id,name}, startTime, endTime, dayOfWeek }
 * - classId: number — the class the conversion targets
 * - className: string — display label for the class
 * - date: Date | string — the date the parent surfaced (used as the default)
 * - onSuccess: () => void — called after a successful conversion (parent refresh)
 */
const ConvertTheoryToLabModal = ({
  open,
  onClose,
  sourceSlots,
  classId,
  className,
  date,
  onSuccess,
}) => {
  const { hasPermission } = usePermissions();
  // Visibility is enforced by the parent; kept here for parity / defensive gating.
  const canConvert =
    hasPermission(TIMETABLE_CONVERT) || hasPermission(TIMETABLE_CONVERT_OWN);

  // Defensive normalization — never trust the incoming array shape (no-crash-guarantee).
  // Sort source slots by startTime so combined span + submitted ids are deterministic.
  const sortedSources = useMemo(() => {
    const arr = Array.isArray(sourceSlots) ? sourceSlots.filter(Boolean) : [];
    return [...arr].sort((a, b) => {
      const am = timeToMinutes(normalizeTime(a?.startTime));
      const bm = timeToMinutes(normalizeTime(b?.startTime));
      const av = Number.isNaN(am) ? Infinity : am;
      const bv = Number.isNaN(bm) ? Infinity : bm;
      return av - bv;
    });
  }, [sourceSlots]);

  const firstSlot = sortedSources[0] || null;
  const lastSlot = sortedSources[sortedSources.length - 1] || null;

  // Combined lab span: first source start → last source end.
  const labStart = useMemo(() => normalizeTime(firstSlot?.startTime), [firstSlot]);
  const labEnd = useMemo(() => normalizeTime(lastSlot?.endTime), [lastSlot]);
  const labStartMin = useMemo(() => timeToMinutes(labStart), [labStart]);
  const labEndMin = useMemo(() => timeToMinutes(labEnd), [labEnd]);

  const durationMinutes = useMemo(() => {
    if (Number.isNaN(labStartMin) || Number.isNaN(labEndMin)) return 0;
    const d = labEndMin - labStartMin;
    return d > 0 ? d : 0;
  }, [labStartMin, labEndMin]);

  const durationLabel = useMemo(
    () => formatDuration(durationMinutes),
    [durationMinutes]
  );

  const isShortLab = useMemo(
    () => durationMinutes > 0 && durationMinutes <= SHORT_LAB_MINUTES,
    [durationMinutes]
  );

  const slotDayNumber = useMemo(
    () => getDayNumber(firstSlot?.dayOfWeek),
    [firstSlot]
  );

  const today = useMemo(() => startOfDay(new Date()), []);
  const maxDate = useMemo(() => addDays(today, 7), [today]);
  // Past dates allowed (gated server-side by marked attendance); far-past floor.
  const minDate = useMemo(() => subYears(today, 1), [today]);

  // --- State ---
  const [count, setCount] = useState(DEFAULT_BATCHES);
  // batches: array of { code: "A1", teacher, subject }.
  const [batches, setBatches] = useState([]);
  const [overrideDate, setOverrideDate] = useState(null);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Teacher dropdown state
  const [teachers, setTeachers] = useState([]);
  const [teachersLoading, setTeachersLoading] = useState(false);
  const [teachersError, setTeachersError] = useState(null);

  // Subject dropdown state
  const [subjects, setSubjects] = useState([]);
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [subjectsError, setSubjectsError] = useState(null);

  // --- Fetch teacher list (mirrors ConvertLabToTheoryModal) ---
  const fetchTeachers = useCallback(async () => {
    setTeachersLoading(true);
    setTeachersError(null);
    try {
      const response = await api.get("/teacher/get-teachers-names");
      const data = response?.data?.data || response?.data || [];
      const teacherList = Array.isArray(data)
        ? data.map((t) => ({
            id: t?.id || t?.teacherId,
            name:
              t?.name ||
              t?.fullName ||
              `${t?.firstName || ""} ${t?.lastName || ""}`.trim(),
          }))
        : [];
      setTeachers(teacherList);
    } catch (err) {
      console.error("[ConvertTheoryToLabModal:fetchTeachers] Error:", err?.message || err);
      setTeachersError(err?.response?.data?.message || "Failed to load teachers");
    } finally {
      setTeachersLoading(false);
    }
  }, []);

  // --- Fetch subject list (mirrors ConvertLabToTheoryModal) ---
  const fetchSubjects = useCallback(async () => {
    setSubjectsLoading(true);
    setSubjectsError(null);
    try {
      const response = await courseService.getAllSubjects({ limit: 500 });
      const data =
        response?.data?.data?.subjects ||
        response?.data?.data ||
        response?.data ||
        [];
      const subjectList = Array.isArray(data)
        ? data.map((s) => ({
            id: s?.id,
            name: s?.name || s?.subjectName || "",
          }))
        : [];
      setSubjects(subjectList);
    } catch (err) {
      console.error("[ConvertTheoryToLabModal:fetchSubjects] Error:", err?.message || err);
      setSubjectsError(err?.response?.data?.message || "Failed to load subjects");
    } finally {
      setSubjectsLoading(false);
    }
  }, []);

  // Fetch dropdown data when the dialog opens.
  useEffect(() => {
    if (open) {
      fetchTeachers();
      fetchSubjects();
    }
  }, [open, fetchTeachers, fetchSubjects]);

  // Initialize count when dialog opens.
  useEffect(() => {
    if (!open) return;
    setCount(DEFAULT_BATCHES);
  }, [open]);

  // (Re)generate batch rows whenever count changes. Preserve already-picked
  // teacher/subject/code where an index still exists; prefill new codes A1, A2...
  useEffect(() => {
    if (!open) return;
    const n = Math.max(MIN_BATCHES, Math.min(MAX_BATCHES, count));
    setBatches((prev) =>
      Array.from({ length: n }, (_, i) => ({
        code: prev[i]?.code ?? defaultBatchCode(i),
        teacher: prev[i]?.teacher ?? null,
        subject: prev[i]?.subject ?? null,
      }))
    );
  }, [open, count]);

  // Default the override date to the passed `date` prop when valid, else the
  // next matching weekday within 7 days.
  useEffect(() => {
    if (!open || slotDayNumber < 0) return;
    let seed = null;
    if (date) {
      const parsed = date instanceof Date ? date : new Date(date);
      if (!Number.isNaN(parsed?.getTime?.())) {
        const d0 = startOfDay(parsed);
        if (d0 >= minDate && d0 <= maxDate && getDay(d0) === slotDayNumber) {
          seed = d0;
        }
      }
    }
    if (!seed) {
      const todayDay = getDay(today);
      if (todayDay === slotDayNumber) {
        seed = today;
      } else {
        for (let i = 1; i <= 7; i++) {
          const candidate = addDays(today, i);
          if (getDay(candidate) === slotDayNumber) {
            seed = candidate;
            break;
          }
        }
      }
    }
    setOverrideDate(seed);
  }, [open, slotDayNumber, today, minDate, maxDate, date]);

  // Reset the form on close.
  useEffect(() => {
    if (!open) {
      setCount(DEFAULT_BATCHES);
      setBatches([]);
      setOverrideDate(null);
      setReason("");
      setSubmitting(false);
    }
  }, [open]);

  // --- Unique-code enforcement (trimmed, case-insensitive) ---
  const duplicateCodeIndexes = useMemo(() => {
    const seen = new Map(); // normalized code -> first index
    const dupes = new Set();
    batches.forEach((b, i) => {
      const code = String(b?.code ?? "").trim().toLowerCase();
      if (!code) return;
      if (seen.has(code)) {
        dupes.add(i);
        dupes.add(seen.get(code));
      } else {
        seen.set(code, i);
      }
    });
    return dupes;
  }, [batches]);

  const allBatchesValid = useMemo(
    () =>
      batches.length >= MIN_BATCHES &&
      batches.every(
        (b) =>
          String(b?.code ?? "").trim().length > 0 &&
          b?.teacher?.id != null &&
          b?.subject?.id != null
      ) &&
      duplicateCodeIndexes.size === 0,
    [batches, duplicateCodeIndexes]
  );

  const isDateValid = useMemo(() => {
    if (!overrideDate) return false;
    const d = startOfDay(overrideDate);
    // Past dates allowed; only enforce the far-past floor and the +7 forward cap.
    if (d < minDate || d > maxDate) return false;
    if (getDay(d) !== slotDayNumber) return false;
    return true;
  }, [overrideDate, minDate, maxDate, slotDayNumber]);

  const spanValid = useMemo(
    () =>
      durationMinutes > 0 &&
      !Number.isNaN(labStartMin) &&
      !Number.isNaN(labEndMin),
    [durationMinutes, labStartMin, labEndMin]
  );

  const canSubmit =
    canConvert &&
    sortedSources.length > 0 &&
    spanValid &&
    allBatchesValid &&
    count >= MIN_BATCHES &&
    count <= MAX_BATCHES &&
    isDateValid &&
    !submitting &&
    !teachersLoading &&
    !subjectsLoading;

  // --- Date restriction (mirrors ConvertLabToTheoryModal) ---
  const shouldDisableDate = useCallback(
    (d) => {
      if (!d) return true;
      const dt = d instanceof Date ? d : new Date(d);
      return getDay(dt) !== slotDayNumber;
    },
    [slotDayNumber]
  );

  // --- Batch field handlers ---
  const handleBatchCodeChange = (index, value) => {
    setBatches((prev) =>
      prev.map((b, i) =>
        i === index ? { ...b, code: value.slice(0, 20) } : b
      )
    );
  };
  const handleBatchTeacherChange = (index, value) => {
    setBatches((prev) =>
      prev.map((b, i) => (i === index ? { ...b, teacher: value } : b))
    );
  };
  const handleBatchSubjectChange = (index, value) => {
    setBatches((prev) =>
      prev.map((b, i) => (i === index ? { ...b, subject: value } : b))
    );
  };

  // --- Submit ---
  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        conversionType: "THEORY_TO_LAB",
        classId,
        overrideDate: format(overrideDate, "yyyy-MM-dd"),
        sourceSlotIds: sortedSources
          .map((s) => s?.id)
          .filter((id) => id != null),
        targetSlots: batches.map((b) => ({
          startTime: labStart,
          endTime: labEnd,
          teacherId: b.teacher.id,
          subjectId: b.subject.id,
          lectureType: "LAB",
          practicalBatchCode: String(b.code).trim(),
        })),
      };
      const trimmedReason = reason.trim();
      if (trimmedReason) payload.reason = trimmedReason;

      await conversionService.createConversion(payload);
      toast.success("Theory converted to lab successfully");
      onSuccess?.();
      onClose?.();
    } catch (err) {
      console.error("[ConvertTheoryToLabModal:handleSubmit] Error:", err?.message || err);
      const message =
        err?.response?.data?.message || "Failed to convert theory to lab";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  // Count selector options (MIN_BATCHES..MAX_BATCHES).
  const countOptions = useMemo(
    () =>
      Array.from(
        { length: MAX_BATCHES - MIN_BATCHES + 1 },
        (_, i) => i + MIN_BATCHES
      ),
    []
  );

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>Convert Theory to Lab</DialogTitle>
      <DialogContent dividers>
        {sortedSources.length === 0 ? (
          <Box sx={{ py: 3 }}>
            <Typography variant="body2" color="error">
              No theory slots were provided to convert.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Read-only source theory info */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {sortedSources.length > 1
                  ? `Theory Lectures (${sortedSources.length})`
                  : "Theory Lecture"}
              </Typography>
              <Grid container spacing={1} sx={{ mb: 1 }}>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Class:</strong> {className || firstSlot?.className || "—"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Day:</strong> {firstSlot?.dayOfWeek || "—"}
                  </Typography>
                </Grid>
              </Grid>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {sortedSources.map((s, i) => (
                  <Typography
                    key={s?.id ?? `src-${i}`}
                    variant="body2"
                    color="text.secondary"
                  >
                    <Chip
                      label={`${normalizeTime(s?.startTime) || "—"} – ${
                        normalizeTime(s?.endTime) || "—"
                      }`}
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    {s?.teacher?.name || "—"} · {s?.subject?.name || "—"}
                  </Typography>
                ))}
              </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Resulting lab span (prominent) */}
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Each batch: {labStart || "—"} – {labEnd || "—"}
                {durationLabel ? ` (${durationLabel})` : ""}
              </Typography>
            </Alert>

            {/* Short-lab warning (still submittable) */}
            {isShortLab ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Short lab session ({durationMinutes} minutes)
              </Alert>
            ) : null}

            {/* Number of batches */}
            <Box sx={{ mb: 2 }}>
              <TextField
                select
                label="Number of batches"
                value={countOptions.includes(count) ? count : ""}
                onChange={(e) => setCount(Number(e.target.value))}
                sx={{ width: 260 }}
                helperText={`${MIN_BATCHES} to ${MAX_BATCHES} batches`}
                disabled={durationMinutes <= 0}
              >
                {countOptions.map((n) => (
                  <MenuItem key={n} value={n}>
                    {n}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            {/* Batch rows */}
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Lab Batches
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
              {batches.map((b, index) => {
                const isDup = duplicateCodeIndexes.has(index);
                const codeEmpty = String(b?.code ?? "").trim().length === 0;
                return (
                  <Paper
                    key={`batch-${index}`}
                    variant="outlined"
                    sx={{ p: 2, borderRadius: 2 }}
                  >
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                      Batch {index + 1}
                    </Typography>
                    <Grid container spacing={2} alignItems="flex-start">
                      <Grid item xs={12} sm={3}>
                        <TextField
                          label="Batch code"
                          value={b?.code ?? ""}
                          onChange={(e) =>
                            handleBatchCodeChange(index, e.target.value)
                          }
                          fullWidth
                          error={isDup || codeEmpty}
                          helperText={
                            isDup
                              ? "Duplicate code"
                              : codeEmpty
                              ? "Required"
                              : "e.g. A1"
                          }
                          inputProps={{ maxLength: 20 }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={5}>
                        <Autocomplete
                          options={teachers}
                          getOptionLabel={(o) => o?.name || ""}
                          value={b?.teacher ?? null}
                          onChange={(_, value) =>
                            handleBatchTeacherChange(index, value)
                          }
                          isOptionEqualToValue={(o, v) => o?.id === v?.id}
                          loading={teachersLoading}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Teacher"
                              placeholder="Search teachers..."
                              error={Boolean(teachersError)}
                              helperText={getListHelperText(
                                teachersError,
                                fetchTeachers
                              )}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {teachersLoading ? (
                                      <CircularProgress size={20} />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Autocomplete
                          options={subjects}
                          getOptionLabel={(o) => o?.name || ""}
                          value={b?.subject ?? null}
                          onChange={(_, value) =>
                            handleBatchSubjectChange(index, value)
                          }
                          isOptionEqualToValue={(o, v) => o?.id === v?.id}
                          loading={subjectsLoading}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Subject"
                              placeholder="Search subjects..."
                              error={Boolean(subjectsError)}
                              helperText={getListHelperText(
                                subjectsError,
                                fetchSubjects
                              )}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {subjectsLoading ? (
                                      <CircularProgress size={20} />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                );
              })}
            </Box>

            {/* Duplicate-code feedback */}
            {duplicateCodeIndexes.size > 0 ? (
              <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
                Batch codes must be unique.
              </Typography>
            ) : null}

            <Divider sx={{ my: 2 }} />

            {/* Date + reason */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <DatePicker
                label="Conversion Date"
                format={DATE_DISPLAY_FORMAT}
                value={overrideDate}
                onChange={(d) => setOverrideDate(d)}
                minDate={minDate}
                maxDate={maxDate}
                shouldDisableDate={shouldDisableDate}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText: getDateHelperText(
                      isDateValid,
                      overrideDate,
                      firstSlot?.dayOfWeek
                    ),
                  },
                }}
              />
              <TextField
                label="Reason (optional)"
                value={reason}
                onChange={(e) => setReason(e.target.value.slice(0, 500))}
                fullWidth
                multiline
                minRows={2}
                inputProps={{ maxLength: 500 }}
                helperText={`${reason.length}/500`}
              />
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={submitting} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={
            submitting ? <CircularProgress size={18} color="inherit" /> : null
          }
        >
          {submitting ? "Converting..." : "Convert to Lab"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConvertTheoryToLabModal;
